export const primary = {
  light: "#0781A8",
  main: "#0781A8",
  dark: "#0781A8",
  contrastText: "#0781A8",
  50: "#000D12",
  100: "#001A23",
  200: "#01222D",
  300: "#035771",
  400: "#0781A8",
  500: "#48A0B2",
  600: "#7EC7D2",
  700: "#AFDFE4",
  800: "#DAF1F2",
  900: "#ECF9F9",
} as const;

// ======== Primitive Color

export const black = {
  10: "#181A1B",
  20: "#1D2022",
  30: "#2E3436",
  60: "#4D5255",
  70: "#5A6063",
  80: "#767B7E",
  90: "#ABB4B6",
  95: "#CACDCD",
  99: "#E6E7E8",
  100: "#F8F9F9",
} as const;

export const blue = {
  30_1: "#000D12",
  30_2: "#001A23",
  30_3: "#01222D",
  60: "#035771",
  70: "#0781A8",
  80: "#48A0B2",
  90: "#7EC7D2",
  95: "#AFDFE4",
  99: "#DAF1F2",
  100: "#ECF9F9",
  200: "#90CAF9",
} as const;

export const secondary = {
  30: "#091A3B",
  60: "#0D285B",
  70: "#14439E",
  80: "#185DE3",
  90: "#3F7FE8",
  95: "#8DBBF2",
  99: "#DCECFB",
  600: "#1F71F1",
} as const;

export const orange = {
  30: "#3E1A00",
  60: "#783300",
  70: "#E15F00",
  80: "#EC7126",
  90: "#FCA577",
  95: "#FECFBD",
  99: "#FFE7E0",
  100: "#FFF5F2",
} as const;

export const green = {
  30: "#005600",
  60: "#198F19",
  70: "#32BE32",
  80: "#59C955",
  90: "#7ED577",
  95: "#C4EBBD",
  99: "#E4F6E0",
} as const;

export const red = {
  30: "#4D1010",
  60: "#A01F1F",
  70: "#E73030",
  80: "#E7555B",
  90: "#F1858D",
  95: "#F3B6BD",
  99: "#FCDFE4",
  700: "#D32F2F",
} as const;

export const lightBlue = {
  30: "#033C4D",
  60: "#0087AE",
  70: "#32BEE6",
  80: "#55CFEB",
  90: "#70D9EB",
  95: "#B3F1F9",
  99: "#E0FAFC",
} as const;

export const pink = {
  30: "#69031E",
  60: "#BD1541",
  70: "#EE3A6A",
  80: "#F15B89",
  90: "#F47DA5",
  95: "#FFB9D4",
  99: "#FDE1EE",
} as const;

export const teal = {
  900: "#004D40",
  800: "#00695C",
  700: "#00796B",
  600: "#009688",
  500: "#009688",
  400: "#26A69A",
  300: "#4DB6AC",
  200: "#80CBC4",
  100: "#B2DFDB",
  50: "#E0F2F1",
} as const;

export const purple = {
  900: "#4A148C",
  800: "#6A1B9A",
  700: "#7B1FA2",
  600: "#8E24AA",
  500: "#9C27B0",
  400: "#AB47BC",
  300: "#BA68C8",
  200: "#CE93D8",
  100: "#E1BEE7",
  50: "#F3E5F5",
} as const;

export const deepPurple = {
  900: "#311B92",
  800: "#4527A0",
  700: "#512DA8",
  600: "#5E35B1",
  500: "#673AB7",
  400: "#7E57C2",
  300: "#9575CD",
  200: "#B39DDB",
  100: "#D1C4E9",
  50: "#EDE7F6",
} as const;

export const lightGreen = {
  900: "#33691E",
  800: "#558B2F",
  700: "#689F38",
  600: "#7CB342",
  500: "#8BC34A",
  400: "#9CCC65",
  300: "#AED581",
  200: "#C5E1A5",
  100: "#DCEDC8",
  50: "#F1F8E9",
} as const;

export const yellow = {
  900: "#F57F17",
  800: "#F9A825",
  700: "#FBC02D",
  600: "#FDD835",
  500: "#FFEB3B",
  400: "#FFEE58",
  300: "#FFF176",
  200: "#FFF59D",
  100: "#FFF9C4",
  50: "#FFFDE7",
} as const;

// ======== Semantics Color

export const background = {
  primary: "#0781A8",
  secondary: "#ECF9F9",
  base: "#F8F9F9",
  tertiary: "#48A0B2",
  main: "#0781A8",
  white: "#FFFFFF",
  orange: "#FFF3E0",
  yellow: "#FFFDE7",
  error: "#FFEBEE",
  disable: "#E6E7E8",
  modal: "#33333350",
  gray: "#CACDCD",
  dark: "#3E3E3E",
  pink: "#F39797",
} as const;

export const text = {
  primary: "#2E3436",
  secondary: "#5A6063",
  tertiary: "#ABB4B6",
  disable: "#CACDCD",
  accentGreen: "#48A0B2",
  accentOrange: "#EC7126",
  white: "#FFFFFF",
  error: "#E57373",
  accent: "#E15F00",
  link: "#1856c3",
  linkHover: "#1856FF",
} as const;

export const button = {
  primary: "#48A0B2",
  hover: "#DAF1F2",
  pressed: "#AFDFE4",
  white: "#FFFFFF",
  hoverWhite: "#F8F9F9",
  pressedWhite: "#E6E7E8",
  disable: "#E6E7E8",
  disableDark: "#ABB4B6",
  hoverError: "#FFEBEE",
  pressedError: "#FFCDD2",
  tertiary: "#EC7126",
  // Legacy aliases
  secondary: "#EC7126",
  danger: "#F1858D",
  danger_hover: "#F1858D4D",
} as const;

export const state = {
  incomplete_secondary: "#FFF3E0",
  success_1: "#32BE32",
  success_2: "#E4F6E0",
  success_3: "#26A69A",
  warning_1: "#EC7126",
  warning_2: "#FFE7E0",
  error_1: "#E73030",
  error_2: "#F3B6BD",
  link: "#185DE3",
  linkHover: "#5BB2FF",
  draft: "#E1BEE7",
  pending: "#FFE0B2",
} as const;

export const divider = {
  high: "#CACDCD",
  middle: "#E6E7E8",
  low: "#F8F9F9",
  lower: "#FFFFFF",
} as const;

export const icon = {
  muted: "#7A8784",
};

export const labelColor = {
  success: "#C8E6C9",
  successSecondary: "#E8F5D0",
  incomplete: "#FFE0B2",
  incompleteSecondary: "#FFF3E0",
  complete: "#E6E7E8",
  progress: "#BBDEFB",
  error: "#FFCDD2",
  errorSecondary: "#FFEBEE",
  disable: "#E6E7E8",
  // Tertiary label colors
  purple: "#E1BEE7",
  deepPurple: "#D1C4E9",
  lightBlue: "#B3E5FC",
  teal: "#B2DFDB",
  lightGreen: "#DCEDC8",
  yellow: "#FFF9C4",
  // Legacy alias
  progress_1: "#B3E5FC",
} as const;

export const borderColor = {
  primary: "#48A0B2",
  hover: "#0781A8",
  error: "#E57373",
  higher: "#ABB4B6",
  high: "#CACDCD",
  middle: "#E6E7E8",
  low: "#F8F9F9",
  lower: "#FFFFFF",
  orange: "#EC7126",
} as const;

export const iconColor = {
  default: "#767B7E",
  primary: "#48A0B2",
  readOnly: "#CACDCD",
  disable: "#E6E7E8",
  error: "#E57373",
  teal: "#66BB6A",
  orange: "#EC7126",
  blue: "#1856c3",
  white: "#FFFFFF",
} as const;
